import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioChange, MatRadioModule } from '@angular/material/radio';
import { IconComponent, LoadingSpinnerComponent } from '@ui';
import { BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, filter, map, startWith, tap } from 'rxjs';
import { Filter } from '../../interfaces/filters-original.interface';
import { SimpleProperty } from '../../interfaces/property.interface';
import { PropertiesService } from '../../services/properties/properties.service';

@Component({
  selector: 'core-property-radio-group',
  standalone: true,
  templateUrl: './property-radio-group.component.html',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatRadioModule,
    MatFormFieldModule,
    MatInputModule,
    LoadingSpinnerComponent,
    IconComponent,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PropertyRadioGroupComponent),
      multi: true,
    },
  ],
})
export class PropertyRadioGroupComponent implements ControlValueAccessor, OnInit {
  @Input() propertyFilters: Filter[] = []; // If you need a subset of properties, you can pass in filters to use in the API call
  @Output() propertySelected = new EventEmitter<SimpleProperty>();
  private readonly propertiesService = inject(PropertiesService);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('scrollContainer', { static: true }) scrollContainer!: ElementRef<HTMLElement>;

  readonly loading$ = new BehaviorSubject<boolean>(true);
  searchTerm = new FormControl('');
  private readonly properties$ = new BehaviorSubject<SimpleProperty[]>([]);
  filteredProperties$ = combineLatest([
    this.searchTerm.valueChanges.pipe(startWith('')),
    this.properties$.pipe(filter((p) => Boolean(p?.length))),
  ]).pipe(
    debounceTime(300),
    distinctUntilChanged(),
    map(([search, properties]) => {
      if (!search) {
        return properties;
      }
      return this.filterProperties(properties, search);
    }),
    takeUntilDestroyed(this.destroyRef)
  );
  totalProperties = 0;

  ngOnInit(): void {
    this.propertiesService
      .getProperties({ transformer: 'simple', paginate: false, filterCriteria: this.propertyFilters })
      .pipe(
        map((res) => res.data),
        tap((properties) => {
          this.loading$.next(false);
          this.totalProperties = properties?.length ?? 0;
          this.properties$.next(properties);
        })
      )
      .subscribe();
  }

  // ControlValueAccessor
  private onChange: (value: number | null) => void = () => {};
  private onTouched: () => void = () => {};
  value: number | null = null;
  disabled = false;

  onRadioChange(event: MatRadioChange): void {
    const newValue = event.value as number;
    this.value = newValue;
    this.onChange(newValue);
    this.onTouched();

    if (this.properties$.value.length > 0) {
      this.propertySelected.emit(this.properties$.value.find((p) => p.id === newValue));
    }
  }

  // ControlValueAccessor implementation
  writeValue(obj: number | null): void {
    this.value = obj;
  }
  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public filterProperties(properties: SimpleProperty[], search: string): SimpleProperty[] {
    return properties.filter(
      (p) =>
        p.name.toLowerCase().indexOf(search.toLowerCase()) > -1 ||
        (p.location?.apt ?? '').toLowerCase().indexOf(search.toLowerCase()) > -1 ||
        (p.location?.street ?? '').toLowerCase().indexOf(search.toLowerCase()) > -1 ||
        (p.location?.city ?? '').toLowerCase().indexOf(search.toLowerCase()) > -1
    );
  }
}
